import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';

import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {DomHandler} from 'primereact/utils';

import metaModel from '~/shared/src/utilities/metaModel';

import {getItemTypeDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {
    YnPermissionName,
    GenericPermissionName
} from '~/shared/src/model/User';
import {StoreState} from '~/frontend/src/store';


export type DefaultProps = {

    /**
     * The ItemTypeName of the selected ItemType.
     */
    value? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : '';

    /**
     * The permission to check for the ItemTypes displayed in the Dropdown.
     */
    permission? : YnPermissionName;

    /**
     * The generic permission to check for the ItemTypes displayed in the Dropdown.
     */
    genericPermission? : GenericPermissionName;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The label for the field.
     */
    label? : string;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    inputStyle? : React.CSSProperties;
};

export const defaultProps : Required<DefaultProps> = {
    value            : '',
    originalValue    : '',
    permission       : 'read',
    genericPermission: 'none',
    markDirty        : false,
    label            : '',
    className        : '',
    inputStyle       : { width: '100%' }
};

type Props = Omit<DropdownProps, 'onChange'> & {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The database schema to show the ItemTypes for.
     */
    schema : Schema;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Only list item types that are on this list AND the user has permissions for
     */
    includeItemTypes? : ( string | number )[];

    /**
     * Don't list item types that are on this list
     */
    excludeItemTypes? : ( string | number )[];

    /**
     * Callback to invoke if value has changed.
     *
     * @param {String} itemTypeName
     */
    onChange : Function;

    style? : { [ key : string ] : string }
};

export type ComponentProps = DefaultProps & Props;
type AllProps = Required<DefaultProps> & Props & {
    onBlur? : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
    onFocus? : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
};

type ItemTypeData = {
    itemTypeId : number,
    itemTypeName : string,
    itemTypeLabel : string
};


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


const ItemTypeDropdown = ( props : AllProps ) => {
    const user                                = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          {
              className,
              excludeItemTypes,
              genericPermission,
              includeItemTypes,
              inputStyle,
              label,
              markDirty,
              onBlur,
              onChange,
              onFocus,
              originalValue,
              permission,
              rawMode,
              schema,
              showAdditionalRawNames,
              style,
              tabIndex,
              uid,
              value,
              // catch all other props for dropdown component
              ...dropdownProps
          }                                   = props,
          dropdownComponentRef                = useRef<Dropdown>( null ),
          fieldLeftTimeoutId                  = useRef<NodeJS.Timeout | undefined>(),
          triggerJustFocused                  = useRef<boolean>( false ),
          [ mobile, setMobile ]               = useState<boolean>( getMobile() ),
          [ focus, setFocus ]                 = useState<boolean>( false ),
          [ overlay, setOverlay ]             = useState<boolean>( false ),
          [ renderTrigger, setRenderTrigger ] = useState<number>( 0 ),
          language                            = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          data                                = useMemo(
              () => {
                  const retVal : ItemTypeData[] = [],
                        itemTypes               = metaModel[ schema ].getItemTypes(),
                        includeItemTypeIds      = includeItemTypes?.map( itemType => metaModel[ schema ].getItemType( itemType )?.id ),
                        excludeItemTypeIds      = excludeItemTypes?.map( itemType => metaModel[ schema ].getItemType( itemType )?.id );

                  for ( const itemType of itemTypes ) {
                      if (
                          itemType.id > 0
                          && user?.hasPermissionOnItemType( permission, itemType.id, schema )
                          && ( genericPermission === 'none' || user?.hasGenericPermissionOnItemType( genericPermission, itemType.id, schema ) )
                          && ( !includeItemTypeIds || includeItemTypeIds.includes( itemType.id as any ) )
                          && ( !excludeItemTypeIds || !excludeItemTypeIds.includes( itemType.id as any ) )
                      ) {
                          retVal.push( {
                              itemTypeId   : itemType.id,
                              itemTypeName : itemType.name,
                              itemTypeLabel: getItemTypeDisplayName( itemType.id, schema, rawMode, showAdditionalRawNames )
                          } );
                      }
                  }

                  retVal.sort( ( val1, val2 ) => {
                      if ( val1.itemTypeLabel > val2.itemTypeLabel ) {
                          return 1;
                      }
                      else if ( val1.itemTypeLabel < val2.itemTypeLabel ) {
                          return -1;
                      }
                      else {
                          return 0;
                      }
                  } );

                  return retVal;
              },
              [
                  schema,
                  includeItemTypes,
                  excludeItemTypes,
                  rawMode,
                  showAdditionalRawNames,
                  permission,
                  genericPermission,
                  user
              ]
          ),
          dropdownValue                       = useMemo(
              () => {
                  let retVal : string | ItemTypeData = '';

                  if ( data ) {
                      retVal = data.length === 1
                          ? data[ 0 ]
                          : data.find( ( rec : ItemTypeData ) => rec.itemTypeName === value ) ?? '';
                  }

                  return retVal;
              },
              [
                  data,
                  value
              ]
          ),
          dropdownClassNames                  = useMemo(
              () => {
                  const retVal = [ 'yn-inputgroup' ];

                  if ( !label ) {
                      retVal.push( className );
                  }
                  if ( focus || overlay ) {
                      retVal.push( 'p-focus' );
                  }
                  else if ( markDirty && value !== originalValue ) {
                      retVal.push( 'yn-dirty-border' );
                  }

                  return retVal;
              },
              [
                  className,
                  focus,
                  label,
                  markDirty,
                  originalValue,
                  overlay,
                  value
              ]
          ),
          onDocumentKeyDown                   = useCallback(
              ( event : React.KeyboardEvent<HTMLElement> ) => {
                  if ( dropdownComponentRef.current ) {
                      let handled : boolean = false,
                          component : Dropdown | undefined,
                          element : HTMLElement | undefined,
                          overlayElement : HTMLElement | undefined,
                          clearTrigger : HTMLElement | undefined,
                          downTrigger : HTMLElement | undefined;

                      switch ( event.key ) {

                          case 'Enter':
                              component = dropdownComponentRef.current;
                              element = component.getElement();
                              overlayElement = component.getOverlay();
                              clearTrigger = ( element?.querySelector( 'i.p-dropdown-clear-icon' ) ?? element?.querySelector( 'svg.p-dropdown-clear-icon' ) ) as HTMLElement | undefined;
                              downTrigger = element?.querySelector( 'div.p-dropdown-trigger' ) as HTMLElement | undefined;

                              if ( event.target === clearTrigger ) {
                                  handled = true;

                                  onChange( '' );
                              }
                              else if ( event.target === downTrigger ) {
                                  handled = true;

                                  if ( overlayElement ) {
                                      // missing TypeScript definition for Dropdown.hide
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      component!.hide();
                                  }
                                  else {
                                      // missing TypeScript definition for Dropdown.show
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      component!.show();
                                  }
                              }

                              if ( handled ) {
                                  event.stopPropagation();
                                  event.preventDefault();
                              }

                              break;

                          case 'Escape':
                              if ( dropdownComponentRef.current?.getOverlay() ) {
                                  dropdownComponentRef.current.hide();
                              }

                              break;
                      }
                  }
              },
              [
                  onChange
              ]
          ),
          onMatchMobileChangeHandler          = () => {
              setMobile( getMobile() );
          },
          onFieldLeft                         = useCallback(
              ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> | React.KeyboardEvent<HTMLElement> | React.SyntheticEvent | undefined ) => {
                  const overlayElement = dropdownComponentRef.current?.getOverlay();

                  if ( overlayElement ) {
                      const activeElement = document.activeElement;

                      if ( overlayElement !== activeElement && !overlayElement.contains( activeElement ) ) {
                          dropdownComponentRef.current!.hide();
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onDropdownChange                    = useCallback(
              ( event : DropdownChangeEvent ) => {
                  let newValue = '';

                  if ( event.value ) {
                      newValue = event.value.itemTypeName;
                  }

                  if ( dropdownComponentRef.current ) {
                      DomHandler.focus( dropdownComponentRef.current.getElement() );
                  }

                  onChange( newValue );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDropdownShow                      = useCallback(
              () => {
                  if ( fieldLeftTimeoutId.current ) {
                      clearTimeout( fieldLeftTimeoutId.current );
                  }

                  setOverlay( true );
                  setRenderTrigger( renderTrigger + 1 );
              },
              [
                  renderTrigger
              ]
          ),
          onDropdownHide                      = useCallback(
              () => {
                  setOverlay( false );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDropdownFocus                     = useCallback(
              ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                  setFocus( true );

                  if ( fieldLeftTimeoutId.current ) {
                      clearTimeout( fieldLeftTimeoutId.current );
                  }

                  onFocus?.( event );
              },
              [
                  onFocus
              ]
          ),
          onDropdownBlur                      = useCallback(
              ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                  if ( !triggerJustFocused.current ) {
                      setFocus( false );

                      if ( fieldLeftTimeoutId.current ) {
                          clearTimeout( fieldLeftTimeoutId.current );
                      }
                      fieldLeftTimeoutId.current = setTimeout(
                          onFieldLeft.bind( null, event ),
                          35
                      );

                      onBlur?.( event );
                  }
                  else {
                      triggerJustFocused.current = false;
                  }
              },
              [
                  onBlur
              ]
          ),
          onDropdownKeyDown                   = useCallback(
              ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                  if ( dropdownComponentRef.current ) {
                      switch ( event.key ) {
                          case 'Enter':
                              dropdownComponentRef.current.focus();
                              break;
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onTriggerFocus                      = useCallback(
              ( element : HTMLElement, event : React.FocusEvent<HTMLElement> ) => {
                  triggerJustFocused.current = true;
                  onDropdownFocus( event );
              },
              [
                  onDropdownFocus
              ]
          ),
          onTriggerBlur                       = useCallback(
              ( element : HTMLElement, event : React.FocusEvent<HTMLElement> ) => {
                  triggerJustFocused.current = false;
                  onDropdownBlur( event );
              },
              [
                  onDropdownBlur
              ]
          ),
          content                             = useMemo(
              () => {
                  return <div className={className + ' field'} style={style}>
                      {label && !mobile &&
                          <label
                              htmlFor={`${uid}_itemTypeDropdown_i`}
                              className="block"
                          >
                              {label}
                          </label>
                      }
                      <Dropdown
                          id={`${uid}_itemTypeDropdown`}
                          key={`${uid}_itemTypeDropdown`}
                          ref={dropdownComponentRef}

                          inputId={`${uid}_itemTypeDropdown_i`}

                          value={dropdownValue}

                          options={data}
                          optionLabel="itemTypeLabel"

                          onChange={onDropdownChange}
                          onShow={onDropdownShow}
                          onHide={onDropdownHide}
                          onFocus={onDropdownFocus}
                          onBlur={onDropdownBlur}
                          onKeyDown={onDropdownKeyDown}

                          filter
                          filterBy="itemTypeLabel"
                          filterMatchMode="startsWith"
                          resetFilterOnHide={true}

                          showClear
                          className={dropdownClassNames.join( ' ' )}
                          placeholder={mobile && label ? label : '[ any ]'}
                          style={inputStyle}
                          disabled={data.length < 2}
                          tabIndex={tabIndex}

                          {...dropdownProps}
                      />
                  </div>;
              },
              [
                  className,
                  data,
                  dropdownClassNames,
                  dropdownProps,
                  dropdownValue,
                  inputStyle,
                  label,
                  language,
                  mobile,
                  onDropdownBlur,
                  onDropdownChange,
                  onDropdownFocus,
                  onDropdownHide,
                  onDropdownKeyDown,
                  onDropdownShow,
                  style,
                  tabIndex,
                  uid,
                  value
              ]
          );

    // on mount
    useEffect(
        () => {
            matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', onMatchMobileChangeHandler );
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            document.addEventListener( 'keydown', onDocumentKeyDown, true );
        },
        []
    );

    // on unmount
    useEffect(
        () => {
            return () => {
                matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', onMatchMobileChangeHandler );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                document.removeEventListener( 'keydown', onDocumentKeyDown, true );
            };
        },
        []
    );

    // set tabIndex to filter if
    useLayoutEffect(
        () => {
            if ( tabIndex && dropdownComponentRef.current ) {
                const overlayTarget      = dropdownComponentRef.current.getOverlay(),
                      target             = dropdownComponentRef.current.getElement(),
                      overlayFilterInput = overlayTarget?.querySelector( 'input.p-dropdown-filter' ) as HTMLInputElement | undefined,
                      targetClearTrigger = ( target?.querySelector( 'i.p-dropdown-clear-icon' ) ?? target?.querySelector( 'svg.p-dropdown-clear-icon' ) ) as HTMLElement | undefined,
                      targetDownTrigger  = target?.querySelector( 'div.p-dropdown-trigger' ) as HTMLElement | undefined;

                overlayFilterInput?.setAttribute( 'tabindex', `${tabIndex + 1}` );

                targetClearTrigger?.setAttribute( 'tabindex', `${tabIndex + 2}` );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetClearTrigger?.removeEventListener( 'focus', onTriggerFocus );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetClearTrigger?.removeEventListener( 'blur', onTriggerBlur );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetClearTrigger?.addEventListener( 'focus', onTriggerFocus );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetClearTrigger?.addEventListener( 'blur', onTriggerBlur );

                targetDownTrigger?.setAttribute( 'tabindex', `${tabIndex + 3}` );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.removeEventListener( 'focus', onTriggerFocus );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.removeEventListener( 'blur', onTriggerBlur );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.addEventListener( 'focus', onTriggerFocus );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.addEventListener( 'blur', onTriggerBlur );
            }
        }
    );

    return content;
};

ItemTypeDropdown.defaultProps = defaultProps;
ItemTypeDropdown.displayName = 'ItemTypeDropdown';

export default ItemTypeDropdown;
